/**
 * useProductNavigation Hook
 *
 * Handles product selection for settings modal and optional navigation.
 * Uses useModalState internally for modal state management.
 */

import { useCallback } from 'react';
import { useModalState, type UseModalStateReturn } from '../core/useModalState';
import type { TrackedProduct } from '../../types/dashboard';

/**
 * Return type for useProductNavigation hook
 */
export interface UseProductNavigationReturn {
  /** Modal state for the product settings dialog */
  modalState: UseModalStateReturn<TrackedProduct>;
  /** Open the product settings modal with product details */
  openNavigationConfirmation: (product: TrackedProduct) => void;
  /** Handle confirmed navigation (opens Shopify admin in new tab) - kept for backward compatibility */
  handleConfirmedNavigation: () => void;
}

/**
 * Hook for managing product selection and settings modal
 *
 * Originally handled navigation to Shopify admin, now repurposed for
 * opening the product settings modal when a product is clicked.
 *
 * @returns Modal state and handlers
 */
export function useProductNavigation(): UseProductNavigationReturn {
  const modalState = useModalState<TrackedProduct>();

  const openNavigationConfirmation = useCallback(
    (product: TrackedProduct) => {
      modalState.open(product);
    },
    [modalState]
  );

  // Keep this for potential future use (navigation to Shopify admin)
  const handleConfirmedNavigation = useCallback(() => {
    if (!modalState.data) return;
    // For now this is a no-op since we're using the modal for settings
    // In the future, we could add a "View in Shopify" button that uses this
    modalState.close();
  }, [modalState]);

  return {
    modalState,
    openNavigationConfirmation,
    handleConfirmedNavigation,
  };
}
